import React from 'react';
import { Link, useForm } from '@inertiajs/react';
import { Seo } from '~/marketing/Seo';
import { Card, Field, Input, Select, Button } from '~/marketing/ui';
import { routes } from '~/marketing/routes';
import { withMarketingLayout } from '~/marketing/MarketingLayout';
import { quoted as Q } from '~/marketing/marketingData';
import { IndustryMatchCard, stillAsking } from '~/shared/IndustryMatch.jsx';

/* Contract: GET /grader → { trades: [{ name, label }], regions: string[], errors },
   plus `industry_match: { key, label, industry }` only when there is a question
   to ask — an always-present prop would stop the prerendered page being served.
   POST /grader with email, company_name, city, region, vertical, and for
   "Something else" (vertical `other`) industry and industry_unlisted.
   vertical carries trade.name, never trade.label. Any other key is dropped
   as unpermitted and the check is refused — so there is deliberately no
   attestation checkbox here; that belongs to the share flow, which is unbuilt.
   Validation is the server's: its messages render as-is in each Field. */

/* What the report contains, which is the question somebody actually has before
   handing over an address. The first answer is a sanctioned string: what a
   visibility figure is counted out of is a claim about the measurement, not a
   description of a feature. */
const REPORT_CONTENTS = [
  {
    h: 'Whether you were named',
    b: Q.denominator_basis,
  },
  {
    h: 'Each AI assistant’s result',
    b: 'A line for each of the five AI assistants: whether it named you, named other businesses but not you, or recommended no business at all.',
  },
  /* These two were listed from the day the page shipped and the report showed
     neither, so they were cut on 2026-10-14 — and came back the same day with
     the report sections that keep them. `grader_surface_test` holds each item
     to a key the report actually sends. */
  {
    h: 'Who else was named',
    b: 'The other businesses the AI assistants recommended, and how many of the answers named each.',
  },
  {
    h: 'Which sources the answers drew on',
    b: 'The sites the answers cited, in two groups: places where you could get listed, and businesses’ own pages.',
  },
  {
    h: 'When it was measured',
    b: 'The date of the check is shown with the result, because AI assistants’ answers change over time.',
  },
];

const row = { display: 'flex', gap: 12, alignItems: 'flex-start' };
const key = { fontSize: 11, color: 'var(--teal)', width: 70, flex: 'none' };
const txt = { fontSize: 'var(--fs-body-sm)', color: 'var(--text-body)' };

export default function New({ trades = [], regions = [], industry_match = null, errors = {} }) {
  const form = useForm({
    email: '', company_name: '', city: '', region: '', vertical: '',
    industry: '', industry_unlisted: false,
  });
  const err = { ...errors, ...form.errors };
  const bind = (k) => ({ value: form.data[k], onChange: (e) => form.setData(k, e.target.value), invalid: !!err[k] || undefined });
  const submit = (e) => { e.preventDefault(); form.post(routes.grader); };

  // "Something else", decided 2026-10-12: the check writes one question from
  // these words. Asked "Did you mean …?" first when the words read as a listed
  // industry. "Use it" switches the industry and does not send — the visitor
  // presses the button they meant to press — and "keep" sends at once.
  const custom = form.data.vertical === 'other';
  const asking = custom && !form.data.industry_unlisted && stillAsking(industry_match, form.data.industry);
  const chooseListed = () => form.setData((d) => ({ ...d, vertical: industry_match.key, industry: '', industry_unlisted: false }));
  const keepOwn = () => { form.setData('industry_unlisted', true); form.post(routes.grader); };

  return (
    <>
      <Seo
        title="Free AI visibility check"
        description="See whether ChatGPT, Claude, Gemini, Perplexity and Google AI Overviews name your company when someone asks who to call in your city. Free, no credit card."
        path={routes.grader}
      />
      <main className="sp-page" style={{ paddingBlock: 'clamp(40px,5vw,72px)', display: 'grid', gap: 'var(--space-12)', gridTemplateColumns: 'repeat(auto-fit,minmax(min(360px,100%),1fr))', alignItems: 'start' }}>
        <div style={{ display: 'grid', gap: 'var(--space-5)', maxWidth: 560 }}>
          <span className="sp-label">Free AI visibility check</span>
          <h1 style={{ fontSize: 'var(--fs-h1)', lineHeight: 1.1 }}>See whether the AI assistants your customers ask name your company.</h1>
          <p style={{ color: 'var(--text-body)', maxWidth: '54ch' }}>We put one question from your industry and city to ChatGPT, Claude, Gemini, Perplexity and Google AI Overviews, and email you what they said.</p>
          <div style={{ display: 'grid', gap: 'var(--space-4)', borderTop: '1px solid var(--border-subtle)', paddingTop: 'var(--space-5)' }}>
            <div style={row}><span className="sp-mono" style={key}>PRIVATE</span><span style={txt}>{Q.report_private}</span></div>
            <div style={row}><span className="sp-mono" style={key}>API</span><span style={txt}>Asked through an API, the same way a plan is measured. <Link href={routes.methodology}>Methodology</Link>.</span></div>
          </div>
        </div>

        <Card>
          <form onSubmit={submit} noValidate style={{ display: 'grid', gap: 'var(--space-5)' }}>
            <div style={{ display: 'grid', gap: 6 }}>
              <h2 style={{ fontSize: 'var(--fs-h3)' }}>Run a check</h2>
              <span className="sp-caption">Free, and no credit card.</span>
            </div>
            {/* The hint read "Exactly as it appears on your site", which assumes
                a website on a form that never asks for one — the product's own
                sanctioned string says many local businesses do not have one and
                none is needed. `Brands/New` asks for the same field with
                "Spelled the way customers write it", which is both true for
                everybody and what `Mention.normalize` actually matches on. */}
            <Field label="Company name" htmlFor="company_name" hint="Spelled the way customers write it." error={err.company_name}>
              <Input id="company_name" name="company_name" autoComplete="organization" placeholder="Valley Air Conditioning" {...bind('company_name')} />
            </Field>
            <div style={{ display: 'grid', gap: 'var(--space-4)', gridTemplateColumns: 'repeat(auto-fit,minmax(min(180px,100%),1fr))' }}>
              <Field label="City" htmlFor="city" error={err.city}>
                <Input id="city" name="city" iconLeft="map-pin" autoComplete="address-level2" placeholder="Nashville" {...bind('city')} />
              </Field>
              <Field label="State" htmlFor="region" error={err.region}>
                <Select id="region" name="region" {...bind('region')}>
                  <option value="">Select a state</option>
                  {regions.map((r) => <option key={r} value={r}>{r}</option>)}
                </Select>
              </Field>
            </div>
            <Field label="Industry" htmlFor="vertical" hint="Decides which question we ask." error={err.vertical}>
              <Select id="vertical" name="vertical" {...bind('vertical')}>
                <option value="">Select an industry</option>
                {trades.map((t) => <option key={t.name} value={t.name}>{t.label}</option>)}
                {/* `BrandSetup::OTHER`, last, as on setup. */}
                <option value="other">Something else</option>
              </Select>
            </Field>
            {custom && (
              <Field label="What kind of business is it?" htmlFor="industry" hint="A few words, the way a customer would say it. For example: dog grooming." error={err.industry}>
                <Input
                  id="industry"
                  name="industry"
                  maxLength={60}
                  value={form.data.industry}
                  invalid={!!err.industry || undefined}
                  onChange={(e) => { const industry = e.target.value; form.setData((d) => ({ ...d, industry, industry_unlisted: false })); }}
                />
              </Field>
            )}
            {asking && (
              <IndustryMatchCard
                match={industry_match}
                onUse={chooseListed}
                onKeep={keepOwn}
                processing={form.processing}
                explanation={`${industry_match.label} is one of our listed industries, and the check asks the question we wrote for it. If “${industry_match.industry}” is something different, keep it and we will write a question for it instead.`}
              />
            )}
            {/* "We email you the report. Nothing else." — the fact survives the
                rule-2 test (somebody handing an address to a stranger has
                wondered), but four words delivered curtly is rule 7. One
                sentence, same promise. */}
            <Field label="Where should we send the report?" htmlFor="email" hint="We email you the report, and nothing else." error={err.email}>
              <Input id="email" name="email" type="email" autoComplete="email" mono placeholder="you@company.com" {...bind('email')} />
            </Field>
            <Button type="submit" size="lg" block loading={form.processing} disabled={asking}>Check your visibility</Button>
          </form>
        </Card>

        {/* Everything below the fold. The page was 449 characters of prose until
            2026-10-05 — a heading, three lines and a form — which is thin for
            the page that should answer "does ChatGPT recommend my business".

            These sections sit outside the two-column grid above, so they run the
            full width underneath the hero and the form rather than stretching
            either. Most of what they say is quoted from `copy-rules.md`: this is
            the page where a stranger decides whether to hand over an address, so
            the claims on it are the sanctioned ones. */}
        <section style={{ gridColumn: '1 / -1', display: 'grid', gap: 'var(--space-10)' }}>
          <div style={{ display: 'grid', gap: 'var(--space-5)', maxWidth: '68ch' }}>
            <h2 style={{ fontSize: 'var(--fs-h2)' }}>What comes back</h2>
            <div style={{ display: 'grid', gap: 'var(--space-5)' }}>
              {REPORT_CONTENTS.map(({ h, b }) => (
                <div key={h} style={{ display: 'grid', gap: 6 }}>
                  <h3 style={{ fontSize: 'var(--fs-body-lg)', margin: 0 }}>{h}</h3>
                  <p style={{ color: 'var(--text-body)', margin: 0 }}>{b}</p>
                </div>
              ))}
            </div>
          </div>

          <div style={{ display: 'grid', gap: 'var(--space-4)', maxWidth: '68ch', borderTop: '1px solid var(--border-subtle)', paddingTop: 'var(--space-8)' }}>
            <h2 style={{ fontSize: 'var(--fs-h2)' }}>One question, five AI assistants</h2>
            <p style={{ color: 'var(--text-body)', margin: 0 }}>
              The check asks one question a customer asks when choosing a business like yours,
              with your city in it, and puts it to all five AI assistants at once.
            </p>
            {/* The full measurement basis, never the short form: that one says
                "three times a question" and the grader asks each engine once. */}
            <p style={{ color: 'var(--text-body)', margin: 0 }}>{Q.measurement_basis}</p>
            <p style={{ color: 'var(--text-body)', margin: 0 }}>{Q.single_sample}</p>
          </div>

          <div style={{ display: 'grid', gap: 'var(--space-4)', maxWidth: '68ch', borderTop: '1px solid var(--border-subtle)', paddingTop: 'var(--space-8)' }}>
            {/* "Free, and yours to keep" until 2026-10-26: free checks are deleted
                after twelve months. */}
            <h2 style={{ fontSize: 'var(--fs-h2)' }}>Free, once every 60 days</h2>
            <p style={{ color: 'var(--text-body)', margin: 0 }}>{Q.free_check}</p>
            {/* "There is no card to enter." led this paragraph — an absence as
                the subject (tone rule 1), and the third telling: the form's own
                caption says "Free, and no credit card" and so does the hero.

                A second sentence promised that a trial started with the same
                address would open on this report — "your first dashboard is
                the report you already read". Nothing has ever rendered a claimed
                check, so it was false from the day it shipped. Cut on the
                founder's call 2026-10-12; `grader_surface_test` holds it out. */}
            <p style={{ color: 'var(--text-body)', margin: 0 }}>
              Fill in the form and the report arrives by email once every AI assistant has answered.
            </p>
          </div>

          <div style={{ display: 'flex', gap: 'var(--space-5)', flexWrap: 'wrap', alignItems: 'center', borderTop: '1px solid var(--border-subtle)', paddingTop: 'var(--space-8)' }}>
            <div style={{ display: 'grid', gap: 6, maxWidth: '54ch' }}>
              <h2 style={{ fontSize: 'var(--fs-h3)', margin: 0 }}>More about how it works</h2>
              <span className="sp-caption">
                The <Link href={routes.faq}>FAQ</Link> covers what is measured, how
                plans work and what happens if you cancel. The{' '}
                <Link href={routes.methodology}>methodology page</Link> explains which AI
                assistants we ask and how an answer becomes a number, and each industry’s page
                lists its questions.
              </span>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}

New.layout = withMarketingLayout;
